<template>
  <loading-view :check="!popup && messagesAvailable">
    <div class="d-flex flex-column min-vh-100 homeBgd" :class="{ switched: switched }">
      <TheHeader :header-links="headerLinks"/>
      <main id="main-content" class="container-fluid">
        <div class="row" v-if="currentUser">
          <aside class="col-md-2 p-3">
            <TheSidebar/>
          </aside>
          <section class="col-md-10">
            <router-view/>
          </section>
        </div>
        <div v-else>
          <router-view/>
        </div>
      </main>
      <TheFooter :footer-links="footerLinks"/>
      <b-modal v-model="backdropApp" ok-only :ok-title="$t('dismiss.label')" size="sm" button-size="sm" ok-variant="secondary" centered>
        <template #modal-header="{ }">
          {{ $t('auth.inprogress.title') }}
        </template>
        <div class="text-center">
          <b-spinner :label="$t('auth.inprogress.title')" variant="secondary" type="grow"></b-spinner>
        </div>
      </b-modal>
      <b-alert fade variant="warning" class="position-fixed fixed-bottom m-0 rounded-0" style="z-index: 2000;" show v-if="offline">
        {{ $t('offline.label') }}
      </b-alert>
      <b-alert fade variant="danger" class="position-fixed fixed-bottom m-0 rounded-0" style="z-index: 2000;" show v-else-if="serverUnavailable">
        {{ $t('no.connection.label') }}
      </b-alert>
    </div>
  </loading-view>
</template>

<script>
import store from './store'
import TheHeader from '@/components/TheHeader'
import TheFooter from '@/components/TheFooter'
import TheSidebar from '@/components/TheSidebar'
import { asyncLanguageLoad } from '@/i18n'
import { authMixin } from '@/mixins/auth.mixin'
// import { stompMixin } from '@/mixins/stomp.mixin'
import { notificationsMixin } from '@rrze/vue-bixins'
import { _AppApiService } from '@/services/_appApi.service'
import { sfcg } from '@/helpers/sfc.helper'
import { LoadingView } from '@rrze/vue-encore'

const onOffListener = () => {
  store.dispatch('auth/ping')
}

const preventNav = (event) => {
  if (store.state.pendingChanges.length < 1) {
    return
  }
  event.preventDefault()
  // Chrome requires returnValue to be set.
  event.returnValue = ''
}

export default {
  name: 'TheApp',
  mixins: [authMixin, /* stompMixin, */ notificationsMixin],
  components: {
    LoadingView,
    TheFooter,
    TheHeader,
    TheSidebar
  },
  data: function () {
    return {
      popup: false,
      backdropApp: false,
      sessionTimeout: false,
      headerLinks: [],
      footerLinks: []
    }
  },
  computed: {
    theme () {
      return this.$store.state.prefs.theme
    },
    offline () {
      return !this.$store.state.auth.status.clientOnline
    },
    serverUnavailable () {
      return !this.$store.state.auth.status.serverOnline
    },
    unauthorizedModal () {
      return this.$store.state.auth.unauthorized.modal
    },
    messagesAvailable () {
      if ('$i18n' in this) {
        return Object.keys(this.$i18n.getLocaleMessage(this.$i18n.locale)).length !== 0
      } else {
        return false
      }
    }
  },
  watch: {
    unauthorizedModal (newValue) {
      if (newValue === true) {
        this.showMsgBoxLogin()
      }
    },
    authInProgress (newValue) {
      this.backdropApp = newValue
    },
    backdropApp (newValue) {
      if (newValue === false) {
        this.$store.dispatch('auth/ping')
      }
    },
    theme: {
      immediate: true,
      handler: 'changeTheme'
    },
    currentUser () {
      this.loadMenu()
    }
  },
  mounted () {
    this.loadMenu()
  },
  created () {
    if (window.opener) {
      this.popup = true
      window.opener.postMessage('portalpopup')
      window.close()
    } else {
      window.addEventListener('online', onOffListener)
      window.addEventListener('offline', onOffListener)
      window.addEventListener('beforeunload', preventNav)
      this.$store.dispatch('auth/ping')
      this.changeLocale(this.$store.state.prefs.lang)
    }
  },
  destroyed () {
    window.removeEventListener('online', onOffListener)
    window.removeEventListener('offline', onOffListener)
    window.removeEventListener('beforeunload', preventNav)
  },
  methods: {
    changeTheme (theme) {
      if (['auto', 'dark'].indexOf(theme) >= 0) {
        document.body.setAttribute('data-theme', theme)
      } else {
        document.body.removeAttribute('data-theme')
      }
    },
    changeLocale (lang) {
      this.$store.commit('prefs/lang', lang)
      asyncLanguageLoad(lang)
    },
    showMsgBoxLogin () {
      this.$bvModal.msgBoxConfirm(this.$i18n.t('unauthorized.text'), {
        title: this.$i18n.t('unauthorized.title'),
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'success',
        okTitle: this.$i18n.t('signin.label'),
        cancelTitle: this.$i18n.t('cancel.label'),
        headerClass: 'p-2 border-bottom-0',
        footerClass: 'p-2 border-top-0',
        centered: true
      })
        .then(value => {
          if (value) {
            this.$store.state.auth.redirectAfterLogin = this.$router.currentRoute.fullPath
            this.$store.dispatch('auth/confirmUnauthorizedRequest')
          } else {
            this.$store.dispatch('auth/declineUnauthorizedRequest')
          }
        })
        .catch(err => {
          // An error occurred
          console.log(err)
        })
    },
    addRoute (r) {
      const vm = this
      const regRoute = vm.$router.getRoutes().find(a => a.name === r.name)
      if (!regRoute) {
        const route = {
          path: r.path,
          name: r.name,
          props: r.props,
          meta: r.meta
        }

        if (r.component) {
          route.component = r.component
        }

        if (r.sfcg) {
          route.component = sfcg(r.sfcg).comp
        }

        vm.$router.addRoute(route)
      }
    },
    addRoutes (items) {
      const vm = this
      items.forEach(item => {
        const r = item.route
        if (r) {
          vm.addRoute(r)
        }
      })
    },
    addNotFoundRoute () {
      const vm = this
      vm.addRoute(
        {
          // path: '/404',
          // alias: '*',
          path: '*',
          alias: '/404',
          name: 'notFound',
          component: () => import(/* webpackChunkName: "notfound" */ './views/NotFound.vue')
        })
    },
    loadMenu () {
      const vm = this
      new _AppApiService(this.$axios).list('menu', {}).then((response) => {
        vm.headerLinks = response.header || []
        vm.addRoutes(vm.headerLinks)

        vm.footerLinks = response.footer || []
        vm.addRoutes(vm.footerLinks)

        vm.addNotFoundRoute()
      }).catch((error) => {
        if (error.status === 404) {
          console.log('Cannot fetch dynamic menu from backend. Probably not required.')
        } else {
          this.makeToast(this.$i18n.t('error.text', {
            status: error.status,
            message: error.message
          }), this.$i18n.t('result.error.title'), 'danger')
        }
      }
      )
    }
  }
}
</script>

<style>
.homeBgd {
  background-color: white;
  background-image: url('/public/background.jpg');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}
</style>
